import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    Tooltip,
    Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import MemoryIcon from '@mui/icons-material/MemoryOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import CodeIcon from '@mui/icons-material/CodeOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { colors } from '../theme';

interface MenuItemConfig {
    path: string;
    label: string;
    icon: React.ReactNode;
    badge?: { label: string; color: 'primary' | 'secondary' | 'default' };
    disabled?: boolean;
    external?: boolean;
}

interface MenuSection {
    title: string;
    items: MenuItemConfig[];
}

const menuSections: MenuSection[] = [
    {
        title: 'Main',
        items: [
            { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
            {
                path: '/bootstrap-tokens',
                label: 'Bootstrap Tokens',
                icon: <KeyIcon />,
                badge: { label: 'Live', color: 'primary' },
            },
            {
                path: '/node-claims',
                label: 'Node Claims',
                icon: <MemoryIcon />,
                badge: { label: 'Live', color: 'primary' },
            },
            {
                path: '/devices',
                label: 'Provisioned Devices',
                icon: <DevicesIcon />,
            },
        ],
    },
    {
        title: 'Administration',
        items: [
            { path: '/users', label: 'Users', icon: <PeopleIcon /> },
            {
                path: '/audit-logs',
                label: 'Audit Logs',
                icon: <HistoryIcon />,
                badge: { label: 'Preview', color: 'secondary' },
            },
            {
                path: '/analytics',
                label: 'Analytics',
                icon: <BarChartIcon />,
                badge: { label: 'Preview', color: 'secondary' },
            },
        ],
    },
    {
        title: 'Developer',
        items: [
            {
                path: '/api-keys',
                label: 'API Keys',
                icon: <CodeIcon />,
                badge: { label: 'Live', color: 'primary' },
            },
            {
                path: '/email-templates',
                label: 'Email Templates',
                icon: <EmailIcon />,
                badge: { label: 'Preview', color: 'secondary' },
            },
            {
                path: '/api-docs',
                label: 'API Documentation',
                icon: <DescriptionIcon />,
                external: true,
                badge: { label: 'Scalar', color: 'secondary' },
            },
        ],
    },
    {
        title: 'System',
        items: [
            {
                path: '/settings',
                label: 'Settings',
                icon: <SettingsIcon />,
                disabled: true,
            },
        ],
    },
];

interface CustomMenuProps {
    collapsed?: boolean;
}

export const CustomMenu = ({ collapsed = false }: CustomMenuProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    const isActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    const handleClick = (item: MenuItemConfig) => {
        if (item.disabled) return;
        if (item.external) {
            window.open('/api-docs/', '_blank');
            return;
        }
        navigate(item.path);
    };

    return (
        <Box>
            {menuSections.map((section, sectionIndex) => (
                <Box key={section.title}>
                    {sectionIndex > 0 && (
                        <Divider
                            sx={{
                                borderColor: 'rgba(255,255,255,0.1)',
                                my: 2,
                                mx: collapsed ? 1 : 2,
                            }}
                        />
                    )}

                    {!collapsed && (
                        <Typography
                            variant="overline"
                            sx={{
                                px: 2,
                                mb: 1,
                                display: 'block',
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                color: colors.slate[500],
                            }}
                        >
                            {section.title}
                        </Typography>
                    )}

                    <List sx={{ px: collapsed ? 0.5 : 0.5 }} disablePadding>
                        {section.items.map((item) => {
                            const active = isActive(item.path);
                            const menuItem = (
                                <ListItemButton
                                    key={item.path}
                                    onClick={() => handleClick(item)}
                                    selected={active}
                                    disabled={item.disabled}
                                    sx={{
                                        borderRadius: '12px',
                                        mb: 0.5,
                                        mx: collapsed ? 'auto' : 1,
                                        px: collapsed ? 1.5 : 2,
                                        py: 1.5,
                                        width: collapsed ? 48 : 'auto',
                                        minHeight: 48,
                                        justifyContent: collapsed ? 'center' : 'flex-start',
                                        position: 'relative',
                                        opacity: item.disabled ? 0.6 : 1,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: alpha(colors.primaryLight, 0.15),
                                            '&:hover': {
                                                backgroundColor: alpha(colors.primaryLight, 0.2),
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: 32,
                                                background: `linear-gradient(180deg, ${colors.primaryLight}, ${colors.tertiary})`,
                                                borderRadius: '0 4px 4px 0',
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: collapsed ? 0 : 40,
                                            color: active ? colors.primaryLight : colors.slate[400],
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>

                                    {!collapsed && (
                                        <>
                                            <ListItemText
                                                primary={item.label}
                                                primaryTypographyProps={{
                                                    sx: {
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        color: active
                                                            ? colors.primaryLight
                                                            : colors.slate[400],
                                                    },
                                                }}
                                            />

                                            {item.badge && (
                                                <Chip
                                                    label={item.badge.label}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        borderRadius: '9999px',
                                                        ...(item.badge.color === 'primary' && {
                                                            background: `linear-gradient(90deg, ${colors.primary}, ${colors.tertiary})`,
                                                            color: 'white',
                                                        }),
                                                        ...(item.badge.color === 'secondary' && {
                                                            background: colors.secondary,
                                                            color: 'white',
                                                        }),
                                                        ...(item.badge.color === 'default' && {
                                                            background: colors.slate[700],
                                                            color: colors.slate[400],
                                                        }),
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </ListItemButton>
                            );

                            if (collapsed) {
                                return (
                                    <Tooltip
                                        key={item.path}
                                        title={item.label}
                                        placement="right"
                                        arrow
                                    >
                                        {menuItem}
                                    </Tooltip>
                                );
                            }

                            return menuItem;
                        })}
                    </List>
                </Box>
            ))}

            {/* Version info */}
            <Box
                sx={{
                    mt: 2,
                    mx: collapsed ? 1 : 2,
                    pt: 2,
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    px: collapsed ? 0 : 1,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                }}
            >
                <Tooltip title={collapsed ? 'Version info' : ''} placement="right">
                    <InfoIcon
                        sx={{
                            fontSize: 14,
                            color: colors.slate[600],
                            mt: 0.25,
                        }}
                    />
                </Tooltip>
                {!collapsed && (
                    <Box
                        sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 11,
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.slate[400],
                                fontSize: 'inherit',
                                fontFamily: 'inherit',
                            }}
                        >
                            v1.0.0
                        </Typography>
                        <Typography
                            sx={{
                                color: colors.slate[600],
                                fontSize: 10,
                                fontFamily: 'inherit',
                            }}
                        >
                            React Admin
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Keep old export for backwards compatibility
export const Menu = CustomMenu;
